import { getRequestDateRange } from "../../../../utils/date/requestDateRange";
import { useReportEffect } from "../../../../hooks/useReportEffect";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";

import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import {
	selectionToNumericIds,
	selectionToSortedIdsKey,
	useFilterContextV2,
} from "../../../../contexts/FilterContextV2";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { exportSupplementationInventoryToExcel } from "../../../../utils/excel/exportSupplementationInventory";
import { IProductTasks } from "../../../../@types/Dashboard/IProductTasks";

export default function DashboardSupplementationInventoryControl() {
  const { t } = useTranslation();
  const {
    rangeDays,
    setRangeDays,
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setDashboard,
    setSideBarOptionSelected,
    setSelectRangeDate
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const [centralWarehouse, setCentralWarehouse] = useState<ICentralWarehouseStock[]>();
  const [depositeInPasture, setDepositeInPasture] = useState<IDepositInPasture[]>();
  const [generalAnalysis, setGeneralAnalysis] = useState<IGeneralAnalysis[]>();
  const [productsTasks, setProductsTasks] = useState<IProductTasks>();

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("SUPPLEMENTATION")
    setSelectRangeDate(2)
    document.title = Constants.TITLE_DASHBOARD;

    if (!rangeDays) {
      const now = new Date();
      setRangeDays({ from: now, to: now });
    }
  }, []);

  useReportEffect((axios) => {
    const farmIds = selectionToNumericIds(selectedFarms);
    const areaIds = selectionToNumericIds(selectedAreas);
    if (!farmIds.length || !areaIds.length || !rangeDays) return;

    const controller = new AbortController();

    const fetchAllData = async () => {
      const startDate = format(rangeDays.from ? new Date(rangeDays.from) : new Date(), 'yyyy-MM-dd');
      const endDate = format(rangeDays.to ? new Date(rangeDays.to) : new Date(), 'yyyy-MM-dd');

      const paramsSupplement = {
        farmIds,
        areaIds
      };

      const paramsProducts = {
        farmIds,
        areaIds,
        startDate: startDate,
        endDate: endDate
      };

      const paramsSerializer = (params: any) => new URLSearchParams(params).toString();

      try {
        const [warehouseRes, analysisRes, depositRes, productsInc, productsDisp] = await Promise.all([
          axios.get("/supplement/central-warehouse-stock", {
            params: { ...paramsSupplement, ...getRequestDateRange(rangeDays.from, rangeDays.to) },
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/supplement/general-analysis", {
            params: { ...paramsSupplement, ...getRequestDateRange(rangeDays.from, rangeDays.to) },
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/supplement/stock-deposit-in-the-pasture", {
            params: { ...paramsSupplement, ...getRequestDateRange(rangeDays.from, rangeDays.to) },
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/tasks/products", {
            params: {
              ...paramsProducts,
              type: "PRODUCT_INCOMING"
            },
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/tasks/products", {
            params: {
              ...paramsProducts,
              type: "PRODUCT_DISPATCH"
            },
            paramsSerializer,
            signal: controller.signal
          })
        ]);

        setCentralWarehouse(
          warehouseRes.data.filter((item: ICentralWarehouseStock) => item.actual_quantity !== 0)
        );
        setGeneralAnalysis(analysisRes.data);
        setDepositeInPasture(depositRes.data);
        setProductsTasks({ incoming: productsInc.data, dispatch: productsDisp.data });
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Erro ao carregar dados de suplementação:', error);
        }
      }
    };

    fetchAllData();

    return () => controller.abort();
  }, [rangeDays, farmsKey, areasKey]);

  const IconMarq = (value: number, min: number, max: number) => {
    let hex = "FFFFFF";

    if (value <= min) {
      hex = "00FF00";
    } else if (value > min && value < max) {
      hex = "FFFF00";
    } else {
      hex = "FF0000";
    }

    return (
      <div style={{
        width: 20,
        height: 20,
        background: `#${hex}`,
        borderRadius: 50,
        border: "1px solid #FFF"
      }} />
    )
  }

  return (
    <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
      <DateModal />

      <form className="dashboardPastureManagement">
        <div className="cattleMap_page">
          <ReportTop icon={<BagFood height={72} />} title={t("navigation.supplementation")} subtitle={t("navigation.inventoryControl")}>
            <FilterDateDashboard
              onExportExcel={() => {
                if (centralWarehouse && generalAnalysis && depositeInPasture) {
                  try {
                    exportSupplementationInventoryToExcel(
                      centralWarehouse,
                      generalAnalysis,
                      depositeInPasture,
                      productsTasks,
                      rangeDays?.from,
                      rangeDays?.to
                    );
                  } catch (error) {
                    console.error('Erro ao exportar:', error);
                    alert(t("reports.exportError"));
                  }
                }
              }}
            />
          </ReportTop>

          {centralWarehouse &&
            <div className="tableDashboard">
              <h2>{t("reports.centralStorageStock")}</h2>
              <table>
                <thead style={{
                  color: "#fff"
                }}>
                  <th style={{
                    background: "#324440",
                    width: 300,
                    textAlign: "left"
                  }}>
                    {t("reports.supplementationLine")}
                  </th>
                  {[
                    t("reports.product"),
                    t("reports.totalStockKg"),
                    t("productPackaging.packaging"),
                    `${t("formsV2.bagWeight")} (kg)`,
                    t("productPackaging.stockBagEquivalent"),
                    t("reports.averageDailyOutput"),
                    t("reports.daysInStock")
                  ].map((label, index) => (
                    <th
                      key={index}
                      style={{
                        background: index % 2 === 0 ? "#808080" : "#324440",
                        width: 180,
                        padding: "10px 20px",
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </thead>

                <tbody>
                  {
                    centralWarehouse?.map((item, index) => (
                      <tr key={item.id} className={index % 2 === 0 ? " bg_white" : "bg_gray"}>
                        <th scope="row">{item.name}</th>
                        <td style={{ textAlign: "right" }}>{item.trade_name ?? '-'}</td>
                        <td style={{ textAlign: "right" }}>{item.actual_quantity ?? '-'}</td>
                        <td style={{ textAlign: "right" }}>
                          {t(item.packaging_type === "BAGGED" ? "formsV2.packagingBagged" : "formsV2.packagingBulk")}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {item.packaging_type === "BAGGED" && item.bag_weight_kg != null
                            ? item.bag_weight_kg
                            : '-'}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          {item.packaging_type === "BAGGED" && item.bag_quantity_equivalent != null
                            ? item.bag_quantity_equivalent
                            : '-'}
                        </td>
                        <td style={{ textAlign: "right" }}>{item.average_daily_output ?? '-'}</td>
                        <td style={{ textAlign: "right" }}>{item.days_in_stock ?? '-'}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          }

          {generalAnalysis &&
            <div className="tableDashboard" id={"analise_geral"}>
              <h2>{t("reports.generalAnalysis")}</h2>
              <table>
                <thead style={{
                  color: "#fff"
                }}>
                  <th style={{
                    background: "#324440",
                    width: 180,
                    textAlign: "left"
                  }}>
                    {t("reports.productClassification")}
                  </th>
                  {[
                    t("reports.totalConsumption"),
                    t("reports.analyzedPeriod"),
                    t("reports.averageSupplementedAnimals"),
                    t("reports.averageSupplementedWeight"),
                    t("reports.totalSupplementedWeight"),
                    t("reports.totalUa"),
                    t("reports.averageConsumptionKg"),
                    t("reports.averageConsumptionUa"),
                    t("reports.replacementFrequency")
                  ].map((label, index) => (
                    <th key={index} scope="col" rowSpan={2} style={{
                      background: index % 2 === 0 ? "#808080" : "#324440",
                      width: 180,
                      padding: "10px 20px"
                    }}>
                      {label}
                    </th>
                  ))}
                </thead>

                <tbody>
                  {
                    generalAnalysis.map((item, index) => (
                      <tr className={index % 2 === 0 ? " bg_white" : "bg_gray"}>
                        <th scope="row">{`${item.name}`}</th>
                        <td style={{ textAlign: "right" }}>{item.total_consumption_period}</td>
                        <td style={{ textAlign: "right" }}>{item.analyzed_period}</td>
                        <td style={{ textAlign: "right" }}>{item.supplemented_animals}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.average_weight_of_supplemented_animals.toFixed(2))}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.total_weight_of_supplemented_animals.toFixed(2))}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.uas_total_number_of_animals_supplemented.toFixed(2))}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.average_consumption_in_the_period_kg.toFixed(3))}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.average_consumption_in_the_period_ua.toFixed(3))}</td>
                        <td style={{ textAlign: "right" }}>{parseFloat(item.replacement_frequency.toFixed(2))}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          }

          <div className="content">
            {depositeInPasture &&
              <div className="tableDashboard" id={"estoque_pasto"}>
                <h2>{t("reports.pastureStorageStock")}</h2>
                <table>
                  <thead style={{
                    color: "#fff"
                  }}>
                    <th style={{
                      background: "#324440",
                      width: "180px",
                      textAlign: "left"
                    }}>
                      {t("reports.paddock")}
                    </th>
                    {[
                      t("reports.supplementType"),
                      t("reports.pastureStorageQuantity"),
                      t("reports.storageCapacity"),
                      t("reports.occupiedCapacity")
                    ].map((label, index) => (
                      <th key={index} style={{
                        background: index % 2 === 0 ? "#808080" : "#324440",
                        width: "180px",
                        padding: "10px 20px"
                      }}>
                        {label}
                      </th>
                    ))}
                  </thead>

                  <tbody>
                    {
                      depositeInPasture?.map((item, index) => (
                        <tr className={index % 2 === 0 ? "bg_white" : "bg_gray"}>
                          <th scope="row">{item.initials}</th>
                          <td style={{ textAlign: "right" }}>{item.name}</td>
                          <td>{item.stock_balance != null ? item.stock_balance : '-'}</td>
                          <td>{item.stock_capacity != null ? item.stock_capacity : 0}</td>
                          <td style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%"
                          }}>
                            {(() => {
                              const stockCapacity = item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : 0;
                              const stockBalance = item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : null;

                              if (stockBalance === null) {
                                return <div style={{ width: 20, height: 20 }} />;
                              }

                              const percentage = stockCapacity !== 0 && stockCapacity !== null ? (stockBalance / stockCapacity) * 100 : 0;

                              return IconMarq(percentage, 30, 50);
                            })()}
                            {(() => {
                              const stockCapacity = item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : 0;
                              const stockBalance = item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : null;

                              if (stockBalance === null) {
                                return 0;
                              }

                              const percentage = stockCapacity !== 0 && stockCapacity !== null ? (stockBalance / stockCapacity) * 100 : 0;

                              if (Math.abs(percentage) < 0.01) {
                                return 0;
                              }

                              return Number(percentage).toPrecision(3);
                            })()} %
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>

                  <tfoot
                    style={{
                      background: "#324440",
                      color: "#fff"
                    }}
                  >
                    <th scope="row" style={{
                      textAlign: "left"
                    }}>{t("reports.grandTotal")}</th>
                    <td />
                    <td>{depositeInPasture?.reduce((acc, item) => acc + (item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : 0), 0)} kg</td>
                    <td>{depositeInPasture?.reduce((acc, item) => acc + (item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : 0), 0)} kg</td>
                    <td>{(() => {
                      const totalBalance = depositeInPasture?.reduce((acc, item) => acc + (item.stock_balance != null && typeof item.stock_balance === 'number' ? item.stock_balance : 0), 0) || 0;
                      const totalCapacity = depositeInPasture?.reduce((acc, item) => acc + (item.stock_capacity != null && typeof item.stock_capacity === 'number' ? item.stock_capacity : 0), 0) || 0;
                      const percentage = totalCapacity !== 0 ? (totalBalance / totalCapacity) * 100 : 0;
                      const formattedPercentage = Math.abs(percentage) < 0.01 ? 0 : Number(percentage).toPrecision(3);
                      return totalCapacity !== 0 ? (
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%"
                        }}>
                          {IconMarq(percentage || 0, 30, 50)}{formattedPercentage} %
                        </div>
                      ) : (
                        <span>0 %</span>
                      );
                    })()}
                    </td>
                  </tfoot>
                  <span>{t("reports.evaluationValuesNote")}</span>

                </table>

                <div className="tableDashboard">
                  <table>
                    <thead style={{
                      color: "#fff"
                    }}>
                      <th scope="col" rowSpan={2} style={{
                        background: "#324440",
                        width: "150px",
                        textAlign: "left"
                      }}>
                        Legenda
                      </th>

                      {[{
                        legenda: "<=30%"
                      },
                      {
                        legenda: ">30% a <50%"
                      },
                      {
                        legenda: ">=50%"
                      }].map((item) => (
                        <th scope="col" rowSpan={2} style={{
                          background: "#324440",
                          width: "150px",
                          textAlign: "left"
                        }}>
                          {item.legenda}
                        </th>
                      ))}
                    </thead>

                    <tbody>
                      <tr className="bg_white">
                        <th scope="row"></th>
                        <td style={{
                          textAlign: "left"
                        }}>{IconMarq(1, 30, 50)}</td>
                        <td style={{
                          textAlign: "left"
                        }}>{IconMarq(35, 30, 50)}</td>
                        <td style={{
                          textAlign: "left"
                        }}>{IconMarq(55, 30, 50)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </div>
        </div>
      </form>
    </div>
  );
}
